/**
 * The PUBLIC_SITE_ENABLED runtime flag (GET /api/public/config).
 *
 * The public site — landing page, public pricing, shareable /homes/:id pages
 * and the state waitlist — stays off until the backend says otherwise. The
 * flag is read at runtime, not at build time, because one frontend image is
 * promoted dev → test → prod.
 *
 * - `useFetchPublicSiteEnabled()` is called once, in App.js. It answers false
 *   until the config arrives and on any error, so a visitor never sees a flash
 *   of the landing page on a deployment where it is off.
 * - App.js passes the answer down through `PublicSiteContext`; components
 *   inside the app read it with `usePublicSiteEnabled()` (false outside a
 *   provider).
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { fetchPublicConfig } from '../agents/publicAgent';

/** Whether the public site is on. Default false: off outside a provider. */
export const PublicSiteContext = createContext(false);

/**
 * Read the flag App.js provides.
 *
 * @returns {boolean}
 */
export function usePublicSiteEnabled() {
  return useContext(PublicSiteContext);
}

/**
 * Fetch the flag once. False while loading and on any error.
 *
 * @returns {boolean}
 */
export function useFetchPublicSiteEnabled() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => fetchPublicConfig())
      .then((config) => {
        if (!cancelled) setEnabled(config?.public_site_enabled === true);
      })
      .catch(() => {
        // Unknown → off; the backend 404s the public routes when it is off.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return enabled;
}
